import { DIET_NAVIGATION_CONFIG } from '../config/quizConfig';

/**
 * Applies diet-based navigation (activate/deactivate slides, auto-fill).
 * Returns { deactivated, activated } so the parent can apply both in the same
 * handleNextSlide call (avoids async state; next slide is correct immediately).
 */
export const handleDietNavigation = (
  slideCode,
  slideValues,
  updateSlideNavigation,
  slideDataSetter
) => {
  const deactivated = [];
  const activated = [];
  if (slideCode !== 37) {
    return { deactivated, activated };
  }
  const dietSelections = slideValues[slideCode];
  if (!dietSelections) {
    return { deactivated, activated };
  }

  if (dietSelections['diet-none']) {
    const config = DIET_NAVIGATION_CONFIG.none;
    if (config?.deactivateSlides?.length) {
      deactivated.push(...config?.deactivateSlides);
      updateSlideNavigation(config?.deactivateSlides, false);
    }
    if (config?.activateSlides?.length) {
      activated.push(...config?.activateSlides);
      updateSlideNavigation(config.activateSlides, true);
    }
    return { deactivated, activated };
  }

  const selectedConfigs = [];
  if (dietSelections['diet-Vegan']) selectedConfigs.push(DIET_NAVIGATION_CONFIG.vegan);
  if (dietSelections['diet-Vegetarian']) selectedConfigs.push(DIET_NAVIGATION_CONFIG.vegetarian);
  if (dietSelections['diet-Dairy-free']) selectedConfigs.push(DIET_NAVIGATION_CONFIG.dairyFree);
  if (dietSelections['diet-Gluten-free']) selectedConfigs.push(DIET_NAVIGATION_CONFIG.glutenFree);
  if (dietSelections['diet-Other']) selectedConfigs.push(DIET_NAVIGATION_CONFIG.other);

  // Compute final hidden/shown slide sets first (when multiple diets are selected),
  // then only auto-fill answers for slides that end up hidden.
  const finalDeactivated = new Set();
  const finalActivated = new Set();
  selectedConfigs.forEach((config) => {
    (config?.deactivateSlides || []).forEach((code) => finalDeactivated.add(code));
    (config?.activateSlides || []).forEach((code) => finalActivated.add(code));
  });

  // Activate wins over deactivate if both are present (combine selections by showing the union)
  finalActivated.forEach((code) => finalDeactivated.delete(code));

  // If a slide becomes visible due to combining diets, clear any previously auto-filled value
  // (but only if the current stored value still equals the auto-fill value).
  const allAutoFillConfigs = [
    DIET_NAVIGATION_CONFIG.vegan,
    DIET_NAVIGATION_CONFIG.vegetarian,
    DIET_NAVIGATION_CONFIG.dairyFree,
    DIET_NAVIGATION_CONFIG.glutenFree,
  ];
  allAutoFillConfigs.forEach((config) => {
    (config?.autoFillValues || []).forEach(({ slideCode: targetSlideCode, key, value, answerCode }) => {
      if (!finalActivated.has(targetSlideCode)) return;
      const currentVal = slideValues?.[targetSlideCode]?.[key]?.value;
      if (currentVal != null && String(currentVal) === String(value)) {
        // updateSlideData in QuizModule removes the field when value === false
        slideDataSetter(targetSlideCode, key, false, answerCode);
      }
    });
  });

  selectedConfigs.forEach((config) => {
    (config?.autoFillValues || []).forEach(({ slideCode: targetSlideCode, key, value, answerCode }) => {
      if (finalDeactivated.has(targetSlideCode)) {
        slideDataSetter(targetSlideCode, key, value, answerCode);
      }
    });
  });

  if (finalDeactivated.size > 0) {
    const codes = Array.from(finalDeactivated);
    deactivated.push(...codes);
    updateSlideNavigation(codes, false);
  }
  if (finalActivated.size > 0) {
    const codes = Array.from(finalActivated);
    activated.push(...codes);
    updateSlideNavigation(codes, true);
  }

  return { deactivated, activated };
};
